import type { AdminNavItem, AdminPermission, AdminRole } from '../types/admin';

export const ADMIN_ROLES: AdminRole[] = ['super_admin', 'admin', 'cms', 'editor', 'faculty', 'support'];

export function isAdminRole(role?: string | null): role is AdminRole {
  return Boolean(role && (ADMIN_ROLES.includes(role as AdminRole) || role === 'admin'));
}

export function isSuperAdmin(role?: string | null) {
  return role === 'super_admin' || role === 'admin';
}

export function canAccessAdmin(role?: string | null) {
  return isAdminRole(role);
}

/** Staff roles that must not use the public student site. */
export function usesAdminConsoleOnly(role?: string | null) {
  return role === 'super_admin' || role === 'admin' || role === 'cms';
}

export function hasAdminPermission(
  permissions: AdminPermission[] | undefined,
  permission: AdminPermission,
) {
  if (!permissions) return true; // Default allow if permissions array not restricted
  return Boolean(permissions.includes(permission));
}

export const ADMIN_NAV: AdminNavItem[] = [
  { id: 'dashboard', label: 'Executive Dashboard', route: '/admin', icon: 'LayoutDashboard', permission: 'dashboard.view' },
  {
    id: 'users',
    label: 'Users & Access',
    icon: 'Users',
    permission: 'users.manage',
    children: [
      { id: 'users-list', label: 'User Directory', route: '/admin/users', icon: 'Users', permission: 'users.manage' },
      { id: 'roles', label: 'Roles & Permissions', route: '/admin/roles', icon: 'ShieldCheck', permission: 'roles.manage', superAdminOnly: true },
    ],
  },
  {
    id: 'cms',
    label: 'Content Management',
    icon: 'Layers',
    permission: 'content.manage',
    children: [
      { id: 'cms-root', label: 'CMS Dashboard', route: '/cms', icon: 'LayoutDashboard', permission: 'content.manage' },
      { id: 'cms-structure', label: 'Structure Manager', route: '/cms/structure', icon: 'Layers', permission: 'content.manage' },
      { id: 'cms-questions', label: 'Question Bank', route: '/cms/questions', icon: 'FileText', permission: 'content.manage' },
      { id: 'cms-pyps', label: 'Previous Year Papers', route: '/cms/pyps', icon: 'ScrollText', permission: 'content.manage' },
      { id: 'cms-live-tests', label: 'Live Mock Tests', route: '/cms/live-tests', icon: 'ClipboardList', permission: 'content.manage' },
      { id: 'cms-test-series', label: 'Test Series', route: '/cms/test-series', icon: 'BookOpen', permission: 'content.manage' },
      { id: 'cms-review', label: 'Review Queue', route: '/cms/review', icon: 'ShieldCheck', permission: 'content.manage' },
      { id: 'cms-media', label: 'Media Assets', route: '/cms/media', icon: 'Upload', permission: 'content.manage' },
      { id: 'cms-insights', label: 'Content Insights', route: '/cms/insights', icon: 'LineChart', permission: 'content.manage' },
      { id: 'admin-homepage', label: 'Homepage CMS', route: '/admin/homepage', icon: 'Home', permission: 'content.manage' },
    ],
  },
  {
    id: 'business',
    label: 'Revenue & Plans',
    icon: 'CreditCard',
    permission: 'billing.view',
    children: [
      { id: 'revenue', label: 'Revenue Overview', route: '/admin/business', icon: 'CreditCard', permission: 'billing.view' },
      { id: 'plans', label: 'Subscription Plans', route: '/admin/plans', icon: 'Briefcase', permission: 'billing.view' },
      { id: 'coupons', label: 'Coupons & Discounts', route: '/admin/coupons', icon: 'Sparkles', permission: 'billing.view' },
      { id: 'payments', label: 'Payments History', route: '/admin/payments', icon: 'Landmark', permission: 'billing.view' },
    ],
  },
  {
    id: 'comms',
    label: 'Comms & Support',
    icon: 'MessageSquare',
    permission: 'support.manage',
    children: [
      { id: 'broadcast', label: 'Broadcast & Queue', route: '/admin/comms', icon: 'Megaphone', permission: 'notifications.manage' },
      { id: 'tickets', label: 'Support Tickets', route: '/admin/support', icon: 'LifeBuoy', permission: 'support.manage' },
    ],
  },
  {
    id: 'platform',
    label: 'System & Health',
    icon: 'Server',
    permission: 'system.health',
    children: [
      { id: 'health', label: 'System Control', route: '/admin/platform', icon: 'Server', permission: 'system.health' },
      { id: 'settings', label: 'Platform Settings', route: '/admin/settings', icon: 'Settings', permission: 'settings.manage' },
      { id: 'audit', label: 'Audit Logs', route: '/admin/audit', icon: 'ScrollText', permission: 'audit.view' },
    ],
  },
  { id: 'reports', label: 'Analytics & Reports', route: '/admin/reports', icon: 'BarChart3', permission: 'analytics.view' },
  { id: 'profile', label: 'Admin Profile', route: '/admin/profile', icon: 'UserCircle', permission: 'dashboard.view' },
];

export function filterAdminNav(
  items: AdminNavItem[],
  permissions: AdminPermission[],
  isSuperAdminUser: boolean,
): AdminNavItem[] {
  return items
    .filter((item) => {
      if (item.superAdminOnly && !isSuperAdminUser) return false;
      if (item.permission && permissions.length > 0 && !permissions.includes(item.permission)) return false;
      return true;
    })
    .map((item) => ({
      ...item,
      children: item.children
        ? filterAdminNav(item.children, permissions, isSuperAdminUser)
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);
}
